import { ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';

interface ComparisonData {
  url1: {
    url: string;
    overallScore: number;
    metrics: {
      structuredData: number;
      mobileFriendly: number;
      accessibility: number;
      contentQuality: number;
    };
    strengths: string[];
    weaknesses: string[];
  };
  url2: {
    url: string;
    overallScore: number;
    metrics: {
      structuredData: number;
      mobileFriendly: number;
      accessibility: number;
      contentQuality: number;
    };
    strengths: string[];
    weaknesses: string[];
  };
}

interface ComparisonResultsProps {
  data: ComparisonData;
  onReset: () => void;
}

export function ComparisonResults({ data, onReset }: ComparisonResultsProps) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'bg-green-50';
    if (score >= 40) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const renderWebsiteColumn = (website: ComparisonData['url1']) => (
    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6 truncate" title={website.url}>
        {formatUrl(website.url)}
      </h2>

      {/* Overall Score */}
      <div className={`${getScoreBg(website.overallScore)} rounded-lg p-4 mb-6`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall AI Readiness</span>
          <span className="text-2xl font-semibold text-gray-900">{website.overallScore}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`${getScoreColor(website.overallScore)} h-3 rounded-full transition-all`}
            style={{ width: `${website.overallScore}%` }}
          />
        </div>
      </div>

      {/* Top Metrics */}
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-700">Structured Data</span>
            <span className="text-sm font-medium text-gray-900">{website.metrics.structuredData}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${getScoreColor(website.metrics.structuredData)} h-2 rounded-full`}
              style={{ width: `${website.metrics.structuredData}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-700">Mobile Friendly</span>
            <span className="text-sm font-medium text-gray-900">{website.metrics.mobileFriendly}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${getScoreColor(website.metrics.mobileFriendly)} h-2 rounded-full`}
              style={{ width: `${website.metrics.mobileFriendly}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-700">Accessibility</span>
            <span className="text-sm font-medium text-gray-900">{website.metrics.accessibility}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${getScoreColor(website.metrics.accessibility)} h-2 rounded-full`}
              style={{ width: `${website.metrics.accessibility}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-700">Content Quality</span>
            <span className="text-sm font-medium text-gray-900">{website.metrics.contentQuality}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`${getScoreColor(website.metrics.contentQuality)} h-2 rounded-full`}
              style={{ width: `${website.metrics.contentQuality}%` }}
            />
          </div>
        </div>
      </div>

      {/* Strengths */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          Strengths
        </h3>
        <ul className="space-y-2">
          {website.strengths.map((strength, idx) => (
            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="text-green-600 mt-0.5">•</span>
              <span>{strength}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Areas of Concern */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          Areas of Concern
        </h3>
        <ul className="space-y-2">
          {website.weaknesses.map((weakness, idx) => (
            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="text-red-600 mt-0.5">•</span>
              <span>{weakness}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const winner = data.url1.overallScore > data.url2.overallScore ? 'url1' : 
                 data.url2.overallScore > data.url1.overallScore ? 'url2' : 'tie';

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={onReset}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              New Comparison
            </button>
            <h1 className="text-4xl font-normal text-black mb-2">Comparison Results</h1>
            {winner !== 'tie' && (
              <p className="text-gray-600">
                {winner === 'url1' ? formatUrl(data.url1.url) : formatUrl(data.url2.url)} has better AI readiness
                {' '}by {Math.abs(data.url1.overallScore - data.url2.overallScore)} points
              </p>
            )}
          </div>

          {/* Comparison Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {renderWebsiteColumn(data.url1)}
            {renderWebsiteColumn(data.url2)}
          </div>

          {/* Action Button */}
          <div className="mt-8 text-center">
            <button
              onClick={onReset}
              className="bg-black text-white font-medium py-3 px-8 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Compare Different Websites
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
